import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect } from 'react'
import { ArrowLeft, ArrowRight, Check, Download, FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Field } from '@/components/ui/field'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Checkbox } from '@/components/ui/checkbox'
import { registrationSchema, type RegistrationSchema } from '@/lib/schemas'
import { useSubmitRegistration } from '@/lib/hooks'
import { usePrograms } from '@/lib/hooks'
import { useRegistrationDraft } from '@/stores/registration-draft'
import { cn } from '@/lib/utils'

const STEPS = [
  { label: 'Player Information', fields: ['firstName', 'middleName', 'lastName', 'age', 'birthdate', 'gender', 'newPlayer'] },
  { label: 'Parent / Guardian', fields: ['parentName', 'phone', 'email', 'address', 'city', 'state', 'zip', 'country'] },
  { label: 'Emergency & Medical', fields: ['emFirst', 'emLast', 'emRelation', 'emPhone', 'medical'] },
  { label: 'League & Waivers', fields: ['league', 'program', 'waiver', 'photo'] },
] as const

const LEAGUE_OPTIONS: {
  value: 'Cal South' | 'SoCal Spring' | 'US Club Soccer'
  label: string
  hint: string
  logo?: string
}[] = [
  {
    value: 'Cal South',
    label: 'Cal South',
    hint: 'State association',
    // No cosmosfc-hosted CalSouth logo — we render a styled text badge instead.
  },
  {
    value: 'SoCal Spring',
    label: 'SoCal Spring',
    hint: 'Spring league',
    logo: 'https://cosmosfc.org/wp-content/uploads/2026/02/SUL.jpg',
  },
  {
    value: 'US Club Soccer',
    label: 'US Club Soccer',
    hint: 'National alliance',
    logo: 'https://cosmosfc.org/wp-content/uploads/2026/02/usclub-logo.webp',
  },
]

export function RegistrationForm() {
  const draft = useRegistrationDraft()
  const [step, setStep] = useState(draft.step)
  const { data: programs = [] } = usePrograms()
  const submit = useSubmitRegistration()

  const form = useForm<RegistrationSchema>({
    resolver: zodResolver(registrationSchema),
    mode: 'onBlur',
    defaultValues: { country: 'United States', ...(draft.data as Partial<RegistrationSchema>) },
  })

  useEffect(() => {
    const sub = form.watch((values) => draft.patch(values as Record<string, unknown>))
    return () => sub.unsubscribe()
  }, [form, draft])

  async function next() {
    const fields = STEPS[step].fields as unknown as Array<keyof RegistrationSchema>
    const ok = await form.trigger(fields)
    if (!ok) return
    if (step < STEPS.length - 1) {
      setStep(step + 1)
      draft.setStep(step + 1)
    }
  }
  function back() {
    if (step > 0) {
      setStep(step - 1)
      draft.setStep(step - 1)
    }
  }

  async function onSubmit(values: RegistrationSchema) {
    try {
      await submit.mutateAsync(values)
      toast.success(`Welcome to Cosmos F.C., ${values.firstName}!`, {
        description: 'A coach will contact you within 48 hours to confirm placement.',
      })
      form.reset({ country: 'United States' } as Partial<RegistrationSchema>)
      draft.clear()
      setStep(0)
    } catch (err) {
      const e = err as { message?: string }
      toast.error(e.message ?? 'Registration failed. Please try again.')
    }
  }

  return (
    <div className="grid gap-8 lg:grid-cols-[300px_1fr] items-start">
      <aside className="lg:sticky lg:top-24 rounded-2xl border border-border bg-card p-6">
        <div className="inline-flex items-center gap-2 rounded bg-cosmos-gold/15 text-cosmos-gold-dark px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest mb-4">
          Non-Profit 501(c)(3)
        </div>
        <span className="eyebrow">Register / Try-out</span>
        <h2 className="mt-3 font-display text-4xl uppercase leading-none">Join Cosmos F.C.</h2>
        <p className="text-sm text-muted-foreground mt-3">
          To attend a tryout or open session you must register online. Fill in your details — a coach will reach out within 48 hours to confirm placement.
        </p>

        <ol className="mt-6 space-y-2">
          {STEPS.map((s, i) => (
            <li
              key={s.label}
              className={cn(
                'flex items-center gap-3 rounded-md border border-transparent px-3 py-2.5 text-sm font-semibold transition-colors',
                i === step && 'border-cosmos-green bg-cosmos-green/8',
                i < step && 'text-muted-foreground'
              )}
            >
              <span
                className={cn(
                  'grid h-6 w-6 place-items-center rounded-full font-display text-xs',
                  i < step ? 'bg-cosmos-green text-white' : i === step ? 'bg-cosmos-green text-white' : 'bg-pitch-900 text-white'
                )}
              >
                {i < step ? <Check className="h-3 w-3" /> : i + 1}
              </span>
              {s.label}
            </li>
          ))}
        </ol>

        <a
          href="https://cosmosfc.org/wp-content/uploads/2026/02/Player-Registration-form-2024-Fill-12.pdf"
          target="_blank"
          rel="noreferrer"
          className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-cosmos-green hover:underline"
        >
          <Download className="h-4 w-4" /> Download PDF form
        </a>
      </aside>

      <form onSubmit={form.handleSubmit(onSubmit)} className="rounded-2xl border border-border bg-card p-6 md:p-8" noValidate>
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.25 }}
          >
            {step === 0 && (
              <fieldset className="space-y-4">
                <legend className="font-display text-xl uppercase text-cosmos-green mb-4">Player Information</legend>
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="First Name" required error={form.formState.errors.firstName?.message}>
                    <Input {...form.register('firstName')} placeholder="Jordan" />
                  </Field>
                  <Field label="Middle Name">
                    <Input {...form.register('middleName')} placeholder="Alex" />
                  </Field>
                  <Field label="Last Name" required error={form.formState.errors.lastName?.message}>
                    <Input {...form.register('lastName')} placeholder="Rivera" />
                  </Field>
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="Age" required error={form.formState.errors.age?.message}>
                    <Input type="number" min={3} max={19} {...form.register('age')} placeholder="12" />
                  </Field>
                  <Field label="Birthdate" required error={form.formState.errors.birthdate?.message}>
                    <Input type="date" {...form.register('birthdate')} />
                  </Field>
                  <Field label="Gender" required error={form.formState.errors.gender?.message}>
                    <Controller
                      control={form.control}
                      name="gender"
                      render={({ field }) => (
                        <RadioGroup value={field.value} onValueChange={field.onChange}>
                          <RadioGroupItem value="Boy" label="Boy" />
                          <RadioGroupItem value="Girl" label="Girl" />
                        </RadioGroup>
                      )}
                    />
                  </Field>
                </div>
                <Field label="New Player?" required error={form.formState.errors.newPlayer?.message}>
                  <Controller
                    control={form.control}
                    name="newPlayer"
                    render={({ field }) => (
                      <RadioGroup value={field.value} onValueChange={field.onChange}>
                        <RadioGroupItem value="Yes" label="Yes — first season" />
                        <RadioGroupItem value="No" label="No — returning player" />
                      </RadioGroup>
                    )}
                  />
                </Field>
              </fieldset>
            )}

            {step === 1 && (
              <fieldset className="space-y-4">
                <legend className="font-display text-xl uppercase text-cosmos-green mb-4">Parent / Guardian</legend>
                <Field label="Full Name" required error={form.formState.errors.parentName?.message}>
                  <Input {...form.register('parentName')} placeholder="Maria Rivera" />
                </Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Phone" required error={form.formState.errors.phone?.message}>
                    <Input type="tel" {...form.register('phone')} placeholder="(760) 555-0142" />
                  </Field>
                  <Field label="Email" required error={form.formState.errors.email?.message}>
                    <Input type="email" {...form.register('email')} placeholder="you@example.com" />
                  </Field>
                </div>
                <Field label="Street Address" required error={form.formState.errors.address?.message}>
                  <Input {...form.register('address')} placeholder="123 Palm Ave." />
                </Field>
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="City" required error={form.formState.errors.city?.message}>
                    <Input {...form.register('city')} placeholder="Vista" />
                  </Field>
                  <Field label="State / Region" required error={form.formState.errors.state?.message}>
                    <Input {...form.register('state')} placeholder="CA" />
                  </Field>
                  <Field label="Zip" required error={form.formState.errors.zip?.message}>
                    <Input {...form.register('zip')} placeholder="92085" />
                  </Field>
                </div>
                <Field label="Country" required error={form.formState.errors.country?.message}>
                  <Input {...form.register('country')} />
                </Field>
              </fieldset>
            )}

            {step === 2 && (
              <fieldset className="space-y-4">
                <legend className="font-display text-xl uppercase text-cosmos-green mb-4">Emergency Contact</legend>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="First Name" required error={form.formState.errors.emFirst?.message}>
                    <Input {...form.register('emFirst')} placeholder="Carlos" />
                  </Field>
                  <Field label="Last Name" required error={form.formState.errors.emLast?.message}>
                    <Input {...form.register('emLast')} placeholder="Rivera" />
                  </Field>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Relation to Player" required error={form.formState.errors.emRelation?.message}>
                    <Input {...form.register('emRelation')} placeholder="Uncle" />
                  </Field>
                  <Field label="Emergency Phone" required error={form.formState.errors.emPhone?.message}>
                    <Input type="tel" {...form.register('emPhone')} placeholder="(760) 555-0199" />
                  </Field>
                </div>
                <Field label="Medical Problems / Physical Limitations" hint="Type 'None' if not applicable.">
                  <Textarea {...form.register('medical')} placeholder="Allergies, asthma, injuries, medications…" />
                </Field>
              </fieldset>
            )}

            {step === 3 && (
              <fieldset className="space-y-6">
                <legend className="font-display text-xl uppercase text-cosmos-green mb-4">League &amp; Program</legend>
                <Field label="Preferred League" required error={form.formState.errors.league?.message}>
                  <Controller
                    control={form.control}
                    name="league"
                    render={({ field }) => (
                      <div className="grid gap-3 sm:grid-cols-3">
                        {LEAGUE_OPTIONS.map((opt) => {
                          const active = field.value === opt.value
                          return (
                            <button
                              type="button"
                              key={opt.value}
                              onClick={() => field.onChange(opt.value)}
                              className={cn(
                                'group relative flex flex-col items-center gap-3 rounded-xl border-2 bg-card p-5 transition-all',
                                active
                                  ? 'border-cosmos-green bg-cosmos-green/6 shadow-md'
                                  : 'border-border hover:border-cosmos-green/50 hover:-translate-y-0.5'
                              )}
                            >
                              <span
                                aria-hidden
                                className={cn(
                                  'absolute top-3 right-3 grid h-5 w-5 place-items-center rounded-full border-2 transition-colors',
                                  active ? 'border-cosmos-green bg-cosmos-green' : 'border-border'
                                )}
                              >
                                {active && <span className="h-2 w-2 rounded-full bg-white" />}
                              </span>
                              <div className="h-14 w-14 grid place-items-center bg-white rounded-md p-1.5 shadow-sm">
                                {opt.logo ? (
                                  <img src={opt.logo} alt={opt.label} className="max-h-full max-w-full object-contain" loading="lazy" />
                                ) : (
                                  <span className="font-display text-xs text-pitch-950 tracking-wider text-center leading-tight">
                                    {opt.label}
                                  </span>
                                )}
                              </div>
                              <div className="text-center">
                                <div className="font-display uppercase text-sm tracking-wide">{opt.label}</div>
                                <div className="text-[10px] text-muted-foreground mt-0.5">{opt.hint}</div>
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    )}
                  />
                </Field>

                <div className="grid gap-4">
                  <Field label="Program Interest" required error={form.formState.errors.program?.message}>
                    <Controller
                      control={form.control}
                      name="program"
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger><SelectValue placeholder="Select a program…" /></SelectTrigger>
                          <SelectContent>
                            {programs.map((p) => (
                              <SelectItem key={p.slug} value={p.name}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </Field>
                </div>
                <div className="pt-2 space-y-3">
                  <Controller
                    control={form.control}
                    name="waiver"
                    render={({ field }) => (
                      <label className="flex items-start gap-3 text-sm text-muted-foreground cursor-pointer">
                        <Checkbox checked={field.value} onCheckedChange={(v) => field.onChange(v === true)} />
                        <span>
                          I acknowledge Cosmos F.C.'s COVID-19 and general liability waiver, and I authorize the coaching staff to seek medical attention if needed.
                          <span className="text-destructive"> *</span>
                        </span>
                      </label>
                    )}
                  />
                  {form.formState.errors.waiver && (
                    <p className="text-xs text-destructive font-medium">{form.formState.errors.waiver.message as string}</p>
                  )}
                  <Controller
                    control={form.control}
                    name="photo"
                    render={({ field }) => (
                      <label className="flex items-start gap-3 text-sm text-muted-foreground cursor-pointer">
                        <Checkbox checked={!!field.value} onCheckedChange={(v) => field.onChange(v === true)} />
                        <span>I consent to my player being photographed / filmed at Cosmos F.C. events for club media use.</span>
                      </label>
                    )}
                  />
                </div>
              </fieldset>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-8 flex items-center justify-between gap-4 pt-6 border-t border-border">
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <FileText className="h-3 w-3" /> Step {step + 1} of {STEPS.length}
          </span>
          <div className="flex gap-2">
            {step > 0 && (
              <Button type="button" variant="outline" onClick={back}>
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
            )}
            {step < STEPS.length - 1 ? (
              <Button type="button" onClick={next}>
                Next <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button type="submit" disabled={submit.isPending}>
                {submit.isPending ? 'Submitting…' : 'Submit Registration →'}
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  )
}

